import React, { useCallback, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { SearchResult } from './types';
import SearchResultCard from './SearchResultCard';
import LoadingSpinner from '../common/LoadingSpinner';
import { CloseIcon } from '../common/CloseIcon';
import { getCookie } from '../utils/utils';
import './SearchResults.scss';
import '../MessagesModal/MessagesModal.scss';

interface SearchResultsProps {
  results: SearchResult[];
  secondaryResults?: SearchResult[];
  hasDetailFilters?: boolean;
  onContactUser: (result: SearchResult) => void;
  onViewOnMap: (result: SearchResult) => void;
  onLoadMore: () => void;
  isLoadingMore: boolean;
  hasMore: boolean;
  totalResults: number;
  secondaryTotalResults?: number;
  noFiltersSet: boolean;
  onScroll?: (scrollTop: number) => void;
  showHeaders?: boolean;
  setShowRemoveListingDialog: (listingId: string | null) => void;
  showRemoveListingDialog: string | null;
  onRemoveResult: (listingId: string) => void;
  fetchLandingPageFeed: () => void;
  handleRefreshFeedClick: () => void;
  performSearch: (loadMore?: boolean) => void;
}

const SearchResults: React.FC<SearchResultsProps> = ({
  results,
  secondaryResults = [],
  hasDetailFilters = false,
  onContactUser,
  onViewOnMap,
  onLoadMore,
  isLoadingMore,
  hasMore,
  totalResults,
  secondaryTotalResults = 0,
  noFiltersSet,
  onScroll,
  setShowRemoveListingDialog,
  showHeaders = true,
  showRemoveListingDialog,
  onRemoveResult,
  fetchLandingPageFeed,
  handleRefreshFeedClick,
  performSearch
}) => {
  const { t } = useTranslation();
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    if (!showRemoveListingDialog) return;
    
    try {
      setIsDeleting(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/off-market-willingness/${showRemoveListingDialog}`,
        {
          method: 'DELETE',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': getCookie('csrf_access_token'),
          },
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to delete');
      }
      
      // Remove from local state instead of reloading page
      onRemoveResult(showRemoveListingDialog);
      setShowRemoveListingDialog(null);
    } catch (err) {
      console.error('Error deleting willingness:', err);
      alert(t('common.error', 'An error occurred. Please try again.'));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const scrollPosition = target.scrollTop + target.clientHeight;
    const scrollHeight = target.scrollHeight;

    // Call the parent's scroll handler if provided
    if (onScroll) {
      onScroll(target.scrollTop);
    }

    // Load more when user is near the bottom (within 100px)
    if (scrollHeight - scrollPosition < 100 && hasMore && !isLoadingMore) {
      onLoadMore();
    }
  }, [hasMore, isLoadingMore, onLoadMore, onScroll]);

  const allResults = [...results, ...secondaryResults];
  const showSeparator = hasDetailFilters && secondaryResults.length > 0;

  if (allResults.length === 0) {
    return (
      <div className="search-results search-results--empty">
        <div className="search-results__empty-state">
          <svg className="search-results__empty-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"></path>
          </svg>
          <h3 className="search-results__empty-title">{t('exploreListings.noResultsTitle')}</h3>
          <p className="search-results__empty-description">{t('exploreListings.noResultsDescription')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`search-results ${!showHeaders ? 'search-results--headers-hidden' : ''}`} onScroll={handleScroll}>
      <div className={`search-results__header ${!showHeaders ? 'search-results__header--hidden' : ''}`}>
        <span className={`search-results__count`}>
          {`${totalResults} ${t('exploreListings.resultsFound')}`
          }
        </span>
      </div>

      <div className={`search-results__grid ${secondaryResults.length > 0 ? 'search-results__grid--also-secondary-results' : ''}`}>
        {results.map(result => (
          <SearchResultCard
            key={result._id}
            result={result}
            onContact={() => onContactUser(result)}
            onViewOnMap={() => onViewOnMap(result)}
            setShowRemoveListingDialog={setShowRemoveListingDialog}
            fetchLandingPageFeed={fetchLandingPageFeed}
            handleRefreshFeedClick={handleRefreshFeedClick}
            performSearch={performSearch}
          />
        ))}
        {results.length === 0 && <div className="search-results__secondary-empty-state">
          <h3 className="search-results__empty-title">{t('exploreListings.noResultsTitleSecondary')}</h3>
          <p className="search-results__empty-description">{t('exploreListings.noResultsDescription')}</p>
        </div>}
      </div>

      {showSeparator && (
        <div className="search-results__separator">
          <div className="search-results__separator-line"></div>
          <div className="search-results__separator-text">
            {t('exploreListings.mightAlsoContain', { secondaryResultsCount: secondaryTotalResults })}
          </div>
          <div className="search-results__separator-line"></div>
        </div>
      )}

      {secondaryResults.length > 0 && (
        <div className="search-results__grid search-results__grid--secondary">
          {secondaryResults.map(result => (
            <SearchResultCard
              key={`secondary-${result._id}`}
              result={result}
              onContact={() => onContactUser(result)}
              onViewOnMap={() => onViewOnMap(result)}
              setShowRemoveListingDialog={setShowRemoveListingDialog}
              fetchLandingPageFeed={fetchLandingPageFeed}
              handleRefreshFeedClick={handleRefreshFeedClick}
              performSearch={performSearch}
            />
          ))}
        </div>
      )}

      {isLoadingMore && (
        <div className="search-results__loading-more">
          <LoadingSpinner />
        </div>
      )}

      {/*       {!hasMore && results.length > 0 && (
        <div className="search-results__end-message">
          {t('exploreListings.endOfResults')}
        </div>
      )} */}

      {/* Single delete confirmation modal for all cards */}
      {showRemoveListingDialog && (
        <div className="end-chat-confirmation-overlay" onClick={() => !isDeleting && setShowRemoveListingDialog(null)}>
          <div className="end-chat-confirmation" onClick={(e) => e.stopPropagation()}>
            <div className="end-chat-confirmation__header">
              <h3>
                <Trans i18nKey="building.offMarket.modal.deleteConfirmTitle">Remove listing</Trans>
              </h3>
              <button
                className="end-chat-confirmation__close"
                onClick={() => setShowRemoveListingDialog(null)}
                disabled={isDeleting}
              >
                <CloseIcon color="#202125" size={16} />
              </button>
            </div>
            <div className="end-chat-confirmation__content">
              <p>
                <Trans i18nKey="building.offMarket.modal.deleteConfirmText">Are you sure you want to remove this listing?</Trans>
              </p>
            </div>
            <div className="end-chat-confirmation__actions">
              <button
                className="end-chat-confirmation__button end-chat-confirmation__button--cancel"
                onClick={() => setShowRemoveListingDialog(null)}
                disabled={isDeleting}
              >
                <Trans i18nKey="building.offMarket.modal.cancel">Cancel</Trans>
              </button>
              <button
                className="end-chat-confirmation__button end-chat-confirmation__button--confirm"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? t('building.offMarket.modal.loading', 'Loading...') : <Trans i18nKey="building.offMarket.modal.delete">Remove</Trans>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchResults; 